/**
 * ApplianceGroupPicker  (SM-24 / DA-2297)
 *
 * The AP read-only multi-appliance group: `id&id&...` (AND — every listed appliance
 * must match) or `id|id|...` (OR — any one matches). Read-only per the design doc —
 * "the write path expects a single integer id" — so callers only mount this for the
 * READ role; write/unselect keep the plain single-appliance CascadeStep.
 */
import React from 'react'
import { Box } from '@mui/material'
import { SearchableSelect, SearchableSelectOption } from 'components/common/SearchableSelect'
import { MultiValueSelect, MultiValueSelectOption } from './MultiValueSelect'

export interface ApplianceGroupPickerProps {
  applianceIds: number[]
  groupOperator?: string
  applianceOptions: MultiValueSelectOption[]
  groupOperatorOptions: SearchableSelectOption[]
  onChange: (applianceIds: number[], groupOperator?: string) => void
  disabled?: boolean
  loading?: boolean
}

export const ApplianceGroupPicker: React.FC<ApplianceGroupPickerProps> = ({
  applianceIds,
  groupOperator,
  applianceOptions,
  groupOperatorOptions,
  onChange,
  disabled = false,
  loading = false,
}) => {
  const stringIds = applianceIds.map(String)

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', minWidth: 260 }}>
      <Box sx={{ flex: 1 }}>
        <MultiValueSelect
          label="Appliance(s)"
          values={stringIds}
          options={applianceOptions}
          onChange={(vals) =>
            onChange(vals.map(Number), vals.length > 1 ? groupOperator || groupOperatorOptions[0]?.value : undefined)
          }
          disabled={disabled}
          loading={loading}
          placeholder={loading ? 'Loading catalog…' : 'Select one or more appliances'}
        />
      </Box>
      {stringIds.length > 1 && (
        <Box sx={{ minWidth: 150 }}>
          <SearchableSelect
            label="Combine as"
            value={groupOperator ?? ''}
            onChange={(v) => onChange(applianceIds, v)}
            options={groupOperatorOptions}
            disabled={disabled}
          />
        </Box>
      )}
    </Box>
  )
}
